import { axiosPrivate } from "../../../api/axios";
import { LogLevel, } from "./Logging-types";
import ReactPixel from "react-facebook-pixel";
import ReactGA from "react-ga4";
import { datadogLogs } from "@datadog/browser-logs";
let isGAInitialized = false;
/**
 * react-ga4 relays events through window.gtag but never loads gtag.js itself.
 * Index.cshtml used to load it per company; this is the single loader that replaces it,
 * so removing that Razor block and calling this must stay together.
 *
 * Page views come from GA4 alone, deliberately: config's automatic page_view covers the
 * first load, and enhanced measurement's "page changes based on browser history events"
 * (on by default in every GA4 property) covers each in-app navigation. Firing our own
 * page_view as well double-counted every page, so none is sent from app code. A tenant
 * that turns that setting off will see only the first page of each session.
 *
 * Config sends that first page_view the moment this runs, reading document.title, so it
 * must run only after the first route has set its title — see RootLayout.
 */
export const initializeGA = (gaKey) => () => {
    if (isGAInitialized || !(gaKey === null || gaKey === void 0 ? void 0 : gaKey.KeyValue))
        return;
    ReactGA.initialize(gaKey.KeyValue);
    isGAInitialized = true;
};
/**
 * Push a GA4 ecommerce event for GTM-configured tags to pick up.
 * The null clear stops items from an earlier push leaking into this event.
 */
export const gtmEvent = (event, ecommerce) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event, ecommerce });
};
/**
 * Log events to Klaviyo
 * @param eventName
 * @param eventInfo
 */
export const createKlaviyoEvent = (companyUsesKlaviyo) => (eventName, eventInfo = {}) => {
    if (companyUsesKlaviyo) {
        axiosPrivate.post(`/OrderFlow/ExportKlaviyoEvent?eventName=${eventName}`, eventInfo).catch(() => { });
    }
};
/**
 * Log events to React Pixel
 * @param title
 * @param data
 */
export const logReactPixel = (pixelKey) => (title, data = {}) => {
    if (pixelKey) {
        ReactPixel.trackCustom(title, data);
    }
};
export const logReactPixelPageView = (pixelKey) => () => {
    if (pixelKey) {
        ReactPixel.trackSingle(pixelKey.KeyValue, "PageView");
    }
};
export const gaEvent = (gaKey) => (title, data = {}) => {
    if (gaKey) {
        ReactGA.event(title, data);
    }
};
/**
 * Log events to Datadog
 * @param msg
 * @param payload
 */
export const datadogLog = (env, build, application) => (msg, type, messageContext, error) => {
    const contextWithEnvData = Object.assign(Object.assign({}, messageContext), { environment: env, build_number: build, application: application });
    switch (type) {
        case LogLevel.INFO:
            datadogLogs.logger.info(msg, contextWithEnvData, error);
            break;
        case LogLevel.DEBUG:
            datadogLogs.logger.debug(msg, contextWithEnvData, error);
            break;
        case LogLevel.WARN:
            datadogLogs.logger.warn(msg, contextWithEnvData, error);
            break;
        case LogLevel.ERROR:
            datadogLogs.logger.error(msg, contextWithEnvData, error);
            break;
    }
};
/**
 * This function configures the DataDog SDK with the provided parameters,
 * setting up the necessary environment, versioning, and service identification
 * for effective logging.
 *
 * @param {string} token - The client token provided by DataDog for authentication.
 * @param {string} environment - The current application environment (e.g., production, development).
 * @param {string} build - The build version of the application.
 * @param {string} applicationName - The name of the application or service.
 */
export const datadogInit = (token, environment, build, applicationName) => {
    datadogLogs.init({
        clientToken: token,
        env: environment,
        version: build,
        service: applicationName,
    });
};
